/**
 * What: Code-drawn replacements for the 1950s-stock-photo illustrations that used to sit at
 *   these three spots — same house palette and shape vocabulary as `app-home/art/`, encoded as data URIs so they drop into the same `image: string`
 *   props those raster files used to fill.
 * Transport / data-flow: Pure string builders; no fetching, state, or merchant data.
 *
 */

const C = {
  paper: '#FAF8F3',
  ink: '#1B1710',
  stone: '#B9B1A3',
  stone2: '#DDD7CB',
  green: '#2E7A44',
  mustard: '#DFA92E',
  muted: '#6b675e',
}
const FONT = 'Inter,-apple-system,BlinkMacSystemFont,sans-serif'

const uri = (svg: string) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)

function text(
  x: number,
  y: number,
  value: string,
  opts: { size?: number; weight?: number; fill?: string; anchor?: 'start' | 'middle' | 'end' } = {},
) {
  const { size = 11, weight = 400, fill = C.ink, anchor = 'start' } = opts
  return `<text x="${x}" y="${y}" font-family="${FONT}" font-size="${size}" font-weight="${weight}" fill="${fill}" text-anchor="${anchor}">${value}</text>`
}

/** Same product-card chrome as `app-home/art/artShared.tsx`'s `Product`, as a plain string. */
function productCard(x: number, y: number, w: number, h: number, label: string | undefined, band: string) {
  const cx = x + w / 2,
    bw = w * 0.34,
    top = y + h * 0.18
  let s = `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="8" fill="#fff" stroke="${C.stone2}"/>`
  s += `<rect x="${cx - bw * 0.22}" y="${top}" width="${bw * 0.44}" height="${h * 0.1}" rx="2" fill="${C.ink}"/>`
  s += `<rect x="${cx - bw / 2}" y="${top + h * 0.1}" width="${bw}" height="${h * 0.58}" rx="${bw * 0.22}" fill="${C.stone2}"/>`
  s += `<rect x="${cx - bw / 2}" y="${top + h * 0.28}" width="${bw}" height="${h * 0.2}" fill="${band}"/>`
  if (label) {
    s += `<circle cx="${x + 14}" cy="${y + 14}" r="10" fill="${C.ink}"/>`
    s += text(x + 14, y + 18, label, { fill: '#fff', weight: 700, anchor: 'middle' })
  }
  return s
}

function checkMark(cx: number, cy: number, r = 9) {
  return (
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${C.green}"/>` +
    `<path d="M${cx - 4.5} ${cy + 0.2}l3 3 5-5.4" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
  )
}

/**
 * A vs B comparison — same composition as the Home capability tile's "products" art, reused here
 * for the same comparison idea rather than inventing a second one. Replaces welcome-ab-test.png.
 */
export const COMPARISON_ART = uri(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 150"><rect width="240" height="150" fill="${C.paper}"/>` +
    productCard(18, 16, 92, 104, 'A', C.stone) +
    productCard(130, 16, 92, 104, 'B', C.mustard) +
    text(120, 74, 'vs', { size: 13, weight: 700, fill: C.muted, anchor: 'middle' }) +
    `<rect x="18" y="128" width="36" height="9" rx="2" fill="${C.stone}"/>` +
    `<rect x="130" y="128" width="92" height="9" rx="2" fill="${C.ink}"/>` +
    checkMark(222, 24) +
    `</svg>`,
)

/**
 * A status card mid-check: a dashed ring (still working it out) over a clipboard tick — the same
 * "setup not finished yet" idea as the deleted Audiences page's ART_SETUP, recoloured to this surface's
 * palette. Replaces empty-state-inspector.png.
 */
export const CONNECTION_CHECK_ART = uri(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 140"><rect width="240" height="140" rx="12" fill="${C.paper}"/>` +
    `<rect x="92" y="38" width="56" height="70" rx="6" fill="#fff" stroke="${C.stone2}" stroke-width="2"/>` +
    `<rect x="104" y="30" width="32" height="12" rx="3" fill="${C.ink}"/>` +
    `<rect x="102" y="56" width="36" height="6" rx="3" fill="${C.stone2}"/>` +
    `<rect x="102" y="68" width="28" height="6" rx="3" fill="${C.stone2}"/>` +
    checkMark(120, 90, 11) +
    `<circle cx="120" cy="69" r="38" fill="none" stroke="${C.mustard}" stroke-width="3" stroke-dasharray="10 7" stroke-linecap="round"/>` +
    `<circle cx="48" cy="40" r="4" fill="${C.stone}"/><circle cx="196" cy="44" r="5.5" fill="${C.mustard}"/><circle cx="188" cy="104" r="4" fill="${C.stone}"/><circle cx="52" cy="106" r="4.5" fill="${C.green}"/>` +
    `</svg>`,
)

/**
 * An almost-empty credit meter beside a plan-upgrade chip. Replaces pricing-shopkeeper.webp.
 */
export const CREDITS_ART = uri(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 120"><rect width="160" height="120" fill="${C.paper}"/>` +
    `<rect x="16" y="46" width="128" height="16" rx="8" fill="${C.stone2}"/>` +
    `<rect x="16" y="46" width="14" height="16" rx="8" fill="${C.ink}"/>` +
    text(16, 38, 'Credits', { size: 10.5, weight: 650, fill: C.muted }) +
    `<circle cx="80" cy="88" r="17" fill="${C.mustard}"/>` +
    text(80, 93, '$', { size: 15, weight: 700, fill: '#fff', anchor: 'middle' }) +
    `<circle cx="54" cy="84" r="10" fill="${C.stone2}"/>` +
    `<circle cx="106" cy="84" r="10" fill="${C.stone2}"/>` +
    `</svg>`,
)
